/**
 * Hatırlatmaların durumu.
 *
 * Kuyruk ve işçi baştan beri çalışıyordu ama görünmüyordu:
 * gönderilemeyen bir hatırlatma `failed` işaretleniyor, günlüğe bir
 * satır düşüyor ve orada bitiyordu. Randevusunu hatırlatma gitmediği
 * için kaçıran bir ailenin sebebini kimse göremiyordu.
 *
 * EKRAN GÖNDERİLEMEYENLERLE AÇILIYOR. Varsayılan süzgeç "hepsi"
 * olsaydı, günde onlarca gönderilmiş satırın arasında üç tane
 * başarısız kaybolurdu — oysa bakılması gereken tam olarak o üçü.
 */

import { useCallback, useEffect, useState } from 'react'
import { ApiHatasi, KANAL_ADI, hatirlatmaApi, type HatirlatmaSatiri } from '../api'

const anZaman = (s: string | null) =>
  s ? new Date(s).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' }) : '—'

export function Hatirlatmalar() {
  const [satirlar, setSatirlar] = useState<HatirlatmaSatiri[] | null>(null)
  const [sayac, setSayac] = useState<Record<string, number>>({})
  const [sablonAdi, setSablonAdi] = useState<Record<string, string>>({})
  const [durumAdi, setDurumAdi] = useState<Record<string, string>>({})
  const [durum, setDurum] = useState('failed')
  const [hata, setHata] = useState<string | null>(null)
  const [islenen, setIslenen] = useState<string | null>(null)

  const yukle = useCallback(async () => {
    setHata(null)
    try {
      const r = await hatirlatmaApi.liste({ durum, limit: 200 })
      setSatirlar(r.hatirlatmalar)
      setSayac(r.sayac)
      setSablonAdi(r.sablonAdi)
      setDurumAdi(r.durumAdi)
    } catch {
      setHata('Hatırlatmalar alınamadı.')
      setSatirlar([])
    }
  }, [durum])

  useEffect(() => {
    void yukle()
  }, [yukle])

  const isle = async (is: () => Promise<unknown>, id: string, hataMetni: string) => {
    setHata(null)
    setIslenen(id)
    try {
      await is()
      await yukle()
    } catch (e) {
      setHata(e instanceof ApiHatasi ? hataMetni : hataMetni)
    } finally {
      setIslenen(null)
    }
  }

  return (
    <>
      <h1>Hatırlatmalar</h1>
      <p className="alt">
        Randevu ve ev çalışması hatırlatmaları. Geçici bir hata (sunucuya ulaşılamaması gibi)
        kendiliğinden üç kez deneniyor; kalıcı bir hata (alıcının adresi yok, kanal hazır
        değil) denenmiyor — sebebi düzeltilip buradan elle tekrar denenebiliyor.
      </p>

      {hata && <div className="uyari">{hata}</div>}

      <div className="dugmeler" style={{ marginBottom: 12 }}>
        {(['failed', 'queued', 'sent', ''] as const).map((d) => (
          <button key={d} className={durum === d ? 'aktif' : ''} onClick={() => setDurum(d)}>
            {d === '' ? 'Hepsi' : (durumAdi[d] ?? d)}
            {d !== '' && sayac[d] !== undefined ? ` (${sayac[d]})` : ''}
          </button>
        ))}
      </div>

      {satirlar === null ? (
        <p className="alt">Yükleniyor…</p>
      ) : satirlar.length === 0 ? (
        <div className="bilgi">
          {durum === 'failed'
            ? 'Gönderilemeyen hatırlatma yok.'
            : durum === 'queued'
              ? 'Sırada bekleyen hatırlatma yok.'
              : 'Bu durumda kayıt yok.'}
        </div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Ne</th>
              <th>Kime</th>
              <th>Planlanan</th>
              <th>Durum</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {satirlar.map((h) => (
              <tr key={h.id}>
                <td>
                  {sablonAdi[h.sablon] ?? h.sablon}
                  <span className="alt"> · {KANAL_ADI[h.kanal] ?? h.kanal}</span>
                </td>
                <td>
                  {h.alici ?? '—'}
                  {h.ad && (
                    <span className="alt">
                      {' · '}
                      {h.ad} {h.soyad}
                    </span>
                  )}
                  {h.aliciEposta && (
                    <div className="alt" style={{ margin: 0 }}>
                      {h.aliciEposta}
                    </div>
                  )}
                </td>
                <td style={{ color: 'var(--muted)', whiteSpace: 'nowrap' }}>
                  {anZaman(h.planlanan)}
                  {h.gonderildi && (
                    <div className="alt" style={{ margin: 0 }}>
                      gitti: {anZaman(h.gonderildi)}
                    </div>
                  )}
                </td>
                <td>
                  <span className={'rozet' + (h.durum === 'sent' ? ' ok' : '')}>
                    {durumAdi[h.durum] ?? h.durum}
                  </span>
                  {h.hata?.mesaj && (
                    <div className="alt" style={{ margin: 0 }}>
                      {h.hata.mesaj}
                    </div>
                  )}
                  {h.durum === 'queued' && h.sonrakiDeneme && h.deneme > 0 && (
                    <div className="alt" style={{ margin: 0 }}>
                      {h.deneme}. deneme oldu · yeniden: {anZaman(h.sonrakiDeneme)}
                    </div>
                  )}
                  {h.durum === 'failed' && h.deneme > 1 && (
                    <div className="alt" style={{ margin: 0 }}>
                      {h.deneme} kez denendi
                    </div>
                  )}
                </td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {h.durum === 'failed' && (
                    <button
                      disabled={islenen === h.id}
                      onClick={() =>
                        void isle(() => hatirlatmaApi.tekrar(h.id), h.id, 'Tekrar denenemedi.')
                      }
                    >
                      {islenen === h.id ? '…' : 'Tekrar dene'}
                    </button>
                  )}
                  {h.durum === 'queued' && (
                    <button
                      disabled={islenen === h.id}
                      onClick={() =>
                        void isle(() => hatirlatmaApi.iptal(h.id), h.id, 'İptal edilemedi.')
                      }
                    >
                      {islenen === h.id ? '…' : 'İptal et'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <p className="alt">
        Gönderilmiş bir hatırlatma iptal edilemiyor: gitmiş bir e-postayı “iptal” diye
        işaretlemek kaydı yalan söyletmek olurdu.
      </p>
    </>
  )
}
